import DataStructure, { sanitize } from "./DataStructure.js";
import { uid } from "quasar";
import { deepFreeze, formatCurrency } from "../util/misc.js";

export const QUOTE_LABOR_ITEM_FIELDS = {
  id: "",
  itemType: "labor",
  line: 0,
  items: [],
  // Cross-sale install: material line items sold on OTHER sales in this
  // project that this labor installs, stored as `{ saleID, itemID }` so the
  // source sale is preserved (the flat `items` list, keyed by document id
  // alone, cannot say which sale an id came from). Linked in place at sale
  // creation — no duplicate line items are made.
  crossSaleItems: [],
  thirdPartyItems: false,
  laborers: [],
  manHours: null,
  quantity: 0,
  category: "",
  description: "",
  type: "",
  margin: null,
  // `cost` is the labor cost only. `materialCost` and `equipmentCost` break out
  // the material and equipment-rental portions of the job so they can be
  // compared against the matching expense categories on the sale. They stay
  // null on quotes written before the breakout existed, where the whole `cost`
  // was the labor cost. The sales price is derived from the SUM of the three
  // (see `totalCost`), so a quote's margin covers the full expected cost.
  cost: null,
  materialCost: null,
  equipmentCost: null,
  price: null,
  notax: true,
};
deepFreeze(QUOTE_LABOR_ITEM_FIELDS);

export const QUOTE_LABOR_TYPES = {
  new: {
    label: "New Construction Installation",
  },
  replace: {
    label: "Replacement Installation",
  },
  service: {
    label: "Service",
  },
};
deepFreeze(QUOTE_LABOR_TYPES);

/**
 * Labor-item cost fields that are spent as expenses rather than billed by a
 * vendor, mapped to the expense category each one is reconciled against.
 *
 * `QuoteTotal.computeTotal` accumulates these into `total.expenseCosts` under
 * these exact keys, and the Sale Accounting tab renders one cost section per
 * category, so the mapping has to be shared rather than restated. Mirrored by
 * literal strings in `functions/sales-helpers.js::computeQuoteTotals`, which
 * cannot import from `src`.
 */
export const LABOR_EXPENSE_COST_CATEGORIES = {
  materialCost: "Materials",
  equipmentCost: "Equipment Rental",
};
deepFreeze(LABOR_EXPENSE_COST_CATEGORIES);

/**
 * Total expected cost of a labor line: labor + materials + equipment rental.
 *
 * This is the basis the sales price is derived from, so it is shared by the
 * quote editors (`profitCalculations.itemCost`, `QuoteEdit.itemCost`) and the
 * totals computation (`QuoteTotal.computeTotal`). Mirrored in the backend by
 * `laborTotalCost` in `functions/sales-helpers.js`.
 *
 * Returns null only when no cost has been entered at all, so callers can still
 * distinguish "not priced yet" from a deliberate $0.
 */
export function laborTotalCost(data = {}) {
  const { cost, materialCost, equipmentCost } = data;
  if (
    (cost === null || cost === undefined) &&
    (materialCost === null || materialCost === undefined) &&
    (equipmentCost === null || equipmentCost === undefined)
  ) {
    return null;
  }
  return (cost || 0) + (materialCost || 0) + (equipmentCost || 0);
}

export default class QuoteLaborItem extends DataStructure {
  constructor(data = {}) {
    data = sanitize(data, QUOTE_LABOR_ITEM_FIELDS);

    if (!data.id) {
      data.id = uid().slice(-12);
    }

    super(data);
  }

  // Editing

  get isValid() {
    return Boolean(
      this.type &&
        this.data.price !== null &&
        this.data.cost !== null &&
        this.data.manHours !== null &&
        this.data.laborers.length > 0 &&
        (this.data.thirdPartyItems ||
          this.data.items.length > 0 ||
          this.data.crossSaleItems.length > 0)
    );
  }

  // Formatting

  get cost() {
    return this.data.cost === null ? null : formatCurrency(this.data.cost);
  }

  get materialCost() {
    return this.data.materialCost === null
      ? null
      : formatCurrency(this.data.materialCost);
  }

  get equipmentCost() {
    return this.data.equipmentCost === null
      ? null
      : formatCurrency(this.data.equipmentCost);
  }

  // Labor + materials + equipment rental; the basis for the sales price.
  get totalCost() {
    const total = laborTotalCost(this.data);
    return total === null ? null : formatCurrency(total);
  }

  get price() {
    return this.data.price === null ? null : formatCurrency(this.data.price);
  }

  get total() {
    return formatCurrency(this.data.price === null ? 0 : this.data.price);
  }

  get type() {
    return QUOTE_LABOR_TYPES[this.data.type] || null;
  }

  summary(length = 200) {
    const description = this.data.description.replace(
      /^\*\*+[^*]+\*\*+\s*/gm,
      ""
    );
    return length === null
      ? description
      : description.slice(0, length) + (description.length > length ? "…" : "");
  }

  toString() {
    return this.summary();
  }
}
